import {
    IntegrationConnection,
    IntegrationConnectionBase,
    ConnectionProvider,
} from "../types/integrationConnection";
import {
    FinancialAccountSourceType,
    FinancialAccountType,
} from "../types/financialAccount";
import {
    FinancialConnection,
    FinancialConnectionBase,
} from "../types/financialConnection";

export enum IntegrationConnectionCapability {
    INVOICES = "invoices",
    BILLS = "bills",
    TRANSACTIONS = "transactions",
    STATEMENTS = "statements",
    ACCOUNTING = "accounting",
    COUNTERPARTIES = "counterparties",
    CLASSES = "classes",
    ATTACHMENTS = "attachments",
    CATALOG_ITEMS = "catalog_items",
}

interface IntegrationConnectionSettings {
    label: string;
    capabilities: IntegrationConnectionCapability[];
    transactions?: IntegrationConnectionTransactionsSettings;
    statements?: IntegrationConnectionStatementsSettings;
    // interval in hours, eg. 6 means "sync every 6 hours"
    recurringSyncInterval: number;
    // number of days to overlap with the previous sync
    overlapDays: number;
}

interface IntegrationConnectionTransactionsSettings {
    multipleAccountsPerConnection: boolean;
    canAddNewAccountUnderConnection: boolean;
    financialAccountSourceType: FinancialAccountSourceType;
}

interface IntegrationConnectionStatementsSettings {
    financialAccountTypes: FinancialAccountType[];
}

export const INTEGRATION_CONNECTIONS_SETTINGS: Record<
    ConnectionProvider,
    IntegrationConnectionSettings
> = {
    [ConnectionProvider.PAYPAL]: {
        label: "PayPal",
        recurringSyncInterval: 6,
        overlapDays: 1,
        capabilities: [IntegrationConnectionCapability.TRANSACTIONS],
        transactions: {
            multipleAccountsPerConnection: false,
            canAddNewAccountUnderConnection: false,
            financialAccountSourceType: FinancialAccountSourceType.PAYPAL,
        },
    },
    [ConnectionProvider.MERCURY]: {
        label: "Mercury",
        recurringSyncInterval: 1,
        overlapDays: 30,
        capabilities: [
            IntegrationConnectionCapability.INVOICES,
            IntegrationConnectionCapability.TRANSACTIONS,
            IntegrationConnectionCapability.STATEMENTS,
        ],
        transactions: {
            multipleAccountsPerConnection: true,
            canAddNewAccountUnderConnection: false,
            financialAccountSourceType: FinancialAccountSourceType.MERCURY,
        },
        statements: {
            financialAccountTypes: [
                FinancialAccountType.CHECKING,
                FinancialAccountType.SAVINGS,
            ],
        },
    },
    [ConnectionProvider.QUICKBOOKS]: {
        label: "QuickBooks",
        recurringSyncInterval: 1,
        overlapDays: 30,
        capabilities: [
            IntegrationConnectionCapability.ACCOUNTING,
            IntegrationConnectionCapability.COUNTERPARTIES,
            IntegrationConnectionCapability.CLASSES,
            IntegrationConnectionCapability.ATTACHMENTS,
            IntegrationConnectionCapability.CATALOG_ITEMS,
            IntegrationConnectionCapability.INVOICES,
            IntegrationConnectionCapability.BILLS,
        ],
    },
    [ConnectionProvider.BILLCOM]: {
        label: "BILL",
        recurringSyncInterval: 6,
        overlapDays: 1,
        capabilities: [
            IntegrationConnectionCapability.BILLS,
            IntegrationConnectionCapability.TRANSACTIONS,
        ],
        transactions: {
            multipleAccountsPerConnection: false,
            canAddNewAccountUnderConnection: false,
            financialAccountSourceType: FinancialAccountSourceType.BILLCOM,
        },
    },
    [ConnectionProvider.GUSTO]: {
        label: "Gusto",
        recurringSyncInterval: 24,
        overlapDays: 0,
        capabilities: [IntegrationConnectionCapability.TRANSACTIONS],
        transactions: {
            multipleAccountsPerConnection: false,
            canAddNewAccountUnderConnection: false,
            financialAccountSourceType: FinancialAccountSourceType.GUSTO,
        },
    },
    [ConnectionProvider.STRIPE]: {
        label: "Stripe",
        recurringSyncInterval: 6,
        overlapDays: 7,
        capabilities: [
            IntegrationConnectionCapability.TRANSACTIONS,
            IntegrationConnectionCapability.INVOICES,
        ],
        transactions: {
            multipleAccountsPerConnection: false,
            canAddNewAccountUnderConnection: false,
            financialAccountSourceType: FinancialAccountSourceType.STRIPE,
        },
    },
    [ConnectionProvider.ADP_RUN]: {
        label: "ADP RUN",
        recurringSyncInterval: 24,
        overlapDays: 7,
        capabilities: [IntegrationConnectionCapability.TRANSACTIONS],
        transactions: {
            multipleAccountsPerConnection: false,
            canAddNewAccountUnderConnection: false,
            financialAccountSourceType: FinancialAccountSourceType.ADP_RUN,
        },
    },
};

export function isIntegrationConnection(
    connection:
        | FinancialConnectionBase
        | IntegrationConnectionBase
        | FinancialConnection
        | IntegrationConnection,
): connection is IntegrationConnection {
    return "provider" in connection;
}

export function getIntegrationConnectionInstitutionName(
    connection: IntegrationConnectionBase,
): string {
    return (
        connection.institutionName ??
        INTEGRATION_CONNECTIONS_SETTINGS[connection.provider].label
    );
}

export const STANDARD_DISCONNECTION_ERROR = "disconnected";

export function connectionHasCapability(
    provider: ConnectionProvider,
    capability: IntegrationConnectionCapability,
) {
    return INTEGRATION_CONNECTIONS_SETTINGS[provider].capabilities.includes(
        capability,
    );
}

interface IntegrationAccountSyncsStatementsParams {
    provider: ConnectionProvider;
    financialType: FinancialAccountType | null | undefined;
}

export function integrationAccountSyncsStatements({
    provider,
    financialType,
}: IntegrationAccountSyncsStatementsParams): boolean {
    if (
        !connectionHasCapability(
            provider,
            IntegrationConnectionCapability.STATEMENTS,
        )
    ) {
        return false;
    }

    const financialAccountTypes =
        INTEGRATION_CONNECTIONS_SETTINGS[provider].statements
            ?.financialAccountTypes;
    if (!financialAccountTypes) {
        return true;
    }

    return (
        financialType != null && financialAccountTypes.includes(financialType)
    );
}

/**
 * Providers that expose a transaction feed, i.e. produce assignable
 * `integration_account` rows. Providers without it (QuickBooks — accounting
 * only) never have accounts, so surfacing them in the account-centric
 * org-integrations connection list only renders empty groups.
 */
export const TRANSACTION_FEED_PROVIDERS: ConnectionProvider[] = Object.values(
    ConnectionProvider,
).filter((provider) =>
    connectionHasCapability(
        provider,
        IntegrationConnectionCapability.TRANSACTIONS,
    ),
);
